/**
 * Push Notification Service (Frontend)
 * Manages FCM token registration and notification setup
 *
 * Features:
 * - Initialize Firebase Cloud Messaging
 * - Request notification permission
 * - Save FCM tokens to Firestore
 * - Register service worker
 * - Handle foreground notifications
 * - Track device information
 *
 * @module pushNotificationService
 */

import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { httpsCallable } from 'firebase/functions';
import { firebaseConfig, functions } from './firebase';
import { logger } from '../utils/logger';

interface DeviceInfo {
  deviceType: 'web' | 'mobile' | 'tablet';
  browser: string;
  os: string;
}

/**
 * Get device information
 */
function getDeviceInfo(): DeviceInfo {
  const userAgent = navigator.userAgent;

  // Detect device type
  let deviceType: 'web' | 'mobile' | 'tablet' = 'web';
  if (/mobile/i.test(userAgent)) deviceType = 'mobile';
  if (/tablet|ipad|playbook|silk/i.test(userAgent)) deviceType = 'tablet';

  // Detect browser
  let browser = 'Unknown';
  if (/chrome/i.test(userAgent) && !/edge|edg/i.test(userAgent)) browser = 'Chrome';
  else if (/safari/i.test(userAgent) && !/chrome/i.test(userAgent)) browser = 'Safari';
  else if (/firefox/i.test(userAgent)) browser = 'Firefox';
  else if (/edge|edg/i.test(userAgent)) browser = 'Edge';
  else if (/opr\//i.test(userAgent)) browser = 'Opera';

  // Detect OS
  let os = 'Unknown';
  if (/windows/i.test(userAgent)) os = 'Windows';
  else if (/mac/i.test(userAgent)) os = 'Mac';
  else if (/linux/i.test(userAgent)) os = 'Linux';
  else if (/android/i.test(userAgent)) os = 'Android';
  else if (/iphone|ipad|ipod/i.test(userAgent)) os = 'iOS';

  return { deviceType, browser, os };
}

/**
 * Initialize Firebase Cloud Messaging
 * Must be called once on app startup
 */
export async function initializeFCM(): Promise<boolean> {
  try {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      logger.info('This browser does not support notifications');
      return false;
    }

    // Check if service workers are supported
    if (!('serviceWorker' in navigator)) {
      logger.warn('Service workers are not supported');
      return false;
    }

    // Register service worker
    await registerServiceWorker();

    // Initialize messaging
    const messaging = getMessaging();

    // Handle foreground notifications
    onMessage(messaging, payload => {
      handleForegroundNotification(payload);
    });

    logger.info('Firebase Cloud Messaging initialized');
    return true;
  } catch (error) {
    logger.error('Error initializing FCM:', error);
    return false;
  }
}

/**
 * Register service worker for background notifications
 */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  try {
    if (!('serviceWorker' in navigator)) {
      logger.warn('Service workers not supported');
      return null;
    }

    const registration = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js',
      { scope: '/' }
    );

    logger.info('Service worker registered successfully');
    return registration;
  } catch (error) {
    logger.error('Error registering service worker:', error);
    return null;
  }
}

/**
 * Request notification permission and save FCM token
 * Call this when user clicks "Enable Notifications" button
 */
export async function requestNotificationPermission(): Promise<{ token: string | null; status: NotificationPermission }> {
  try {
    // Check current permission status
    const currentPermission = Notification.permission;

    if (currentPermission === 'denied') {
      logger.warn('Notification permission was denied');
      return { token: null, status: 'denied' };
    }

    let permission: NotificationPermission = currentPermission as NotificationPermission;

    // Request permission if not already granted
    if (currentPermission === 'default') {
      permission = await Notification.requestPermission();
    }

    if (permission !== 'granted') {
      logger.warn('Notification permission not granted');
      return { token: null, status: permission };
    }

    // Get FCM token
    const messaging = getMessaging();
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    });

    if (token) {
      // Save token to Firestore
      await saveFCMToken(token);
      logger.info('FCM token obtained and saved');
      return { token, status: 'granted' };
    } else {
      logger.warn('Failed to get FCM token');
      return { token: null, status: 'granted' };
    }
  } catch (error) {
    logger.error('Error requesting notification permission:', error);
    return { token: null, status: Notification.permission };
  }
}

/**
 * Save FCM token to Firestore via Cloud Function
 */
async function saveFCMToken(token: string): Promise<boolean> {
  try {
    const deviceInfo = getDeviceInfo();

    const savePushToken = httpsCallable(functions, 'savePushToken');
    const response = await savePushToken({
      token,
      deviceType: deviceInfo.deviceType,
      browser: deviceInfo.browser,
      os: deviceInfo.os,
    });

    logger.info('FCM token saved successfully', response.data);
    return true;
  } catch (error) {
    logger.error('Error saving FCM token:', error);
    return false;
  }
}

/**
 * Remove FCM token from Firestore (when user disables notifications)
 */
export async function removeFCMToken(tokenId: string): Promise<boolean> {
  try {
    const removePushToken = httpsCallable(functions, 'removePushToken');
    await removePushToken({ tokenId });
    logger.info('FCM token removed successfully');
    return true;
  } catch (error) {
    logger.error('Error removing FCM token:', error);
    return false;
  }
}

/**
 * Handle foreground notifications (when app is open)
 * Backend sends notifications to the app
 */
function handleForegroundNotification(payload: any): void {
  try {
    logger.info('Foreground notification received:', payload);

    const { notification, data } = payload;

    if (!notification) {
      logger.warn('No notification data in payload');
      return;
    }

    // Show notification in browser
    if (Notification.permission === 'granted') {
      const options: NotificationOptions = {
        icon: notification.imageUrl || '/favicon.ico',
        badge: notification.badge || '/favicon.ico',
        tag: data?.tag || 'notification',
        body: notification.body,
        data: data,
        requireInteraction: false,
      };

      // Create notification object
      const notif = new Notification(notification.title || 'Notification', options);

      // Handle click
      notif.onclick = () => {
        window.focus();
        handleNotificationClick(data);
      };

      // Log to Firestore via service worker
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.controller?.postMessage({
          type: 'LOG_NOTIFICATION',
          payload,
        });
      }
    }
  } catch (error) {
    logger.error('Error handling foreground notification:', error);
  }
}

/**
 * Handle notification click
 * Navigate to relevant page based on notification data
 */
function handleNotificationClick(data: Record<string, string> | undefined): void {
  try {
    if (!data) return;

    const action = data.action || 'home';

    // Map actions to routes
    const routes: Record<string, string> = {
      viewWallet: '/wallet',
      viewReports: '/reports',
      viewDashboard: '/dashboard',
      viewTransactions: '/transactions',
      viewProfile: '/profile',
      home: '/',
    };

    const route = routes[action] || '/';
    window.location.href = route;
  } catch (error) {
    logger.error('Error handling notification click:', error);
  }
}

/**
 * Send test notification
 * Verify that push notifications are working
 */
export async function sendTestNotification(): Promise<boolean> {
  try {
    const sendTest = httpsCallable(functions, 'sendTestNotification');
    const response = await sendTest({});

    if ((response.data as any).success) {
      logger.info('Test notification sent successfully');
      return true;
    } else {
      logger.warn('Test notification failed:', (response.data as any).message);
      return false;
    }
  } catch (error) {
    logger.error('Error sending test notification:', error);
    return false;
  }
}

/**
 * Get user's registered devices
 */
export async function getUserDevices(): Promise<any[]> {
  try {
    const getUserTokens = httpsCallable(functions, 'getUserTokens');
    const response = await getUserTokens({});
    return (response.data as any).devices || [];
  } catch (error) {
    logger.error('Error fetching user devices:', error);
    return [];
  }
}

/**
 * Check if notifications are enabled
 */
export function areNotificationsEnabled(): boolean {
  return Notification.permission === 'granted';
}

/**
 * Get notification permission status
 */
export function getNotificationPermissionStatus(): NotificationPermission {
  return Notification.permission as NotificationPermission;
}

/**
 * Enable notifications and save preferences
 */
export async function enableNotifications(): Promise<boolean> {
  try {
    const result = await requestNotificationPermission();
    return result.status === 'granted' && result.token !== null;
  } catch (error) {
    logger.error('Error enabling notifications:', error);
    return false;
  }
}

/**
 * Disable notifications
 */
export async function disableNotifications(): Promise<boolean> {
  try {
    // Get user's tokens and remove them
    const devices = await getUserDevices();
    
    for (const device of devices) {
      await removeFCMToken(device.tokenId);
    }

    logger.info('Notifications disabled');
    return true;
  } catch (error) {
    logger.error('Error disabling notifications:', error);
    return false;
  }
}
